import { useEffect, useMemo, useState } from "react";
import axios from "../axiosConfig";

import { BASE_SERVER_URL, ProductTypes } from "../sampleData";
import ProductPhoto from "./ProductPhoto";

/* ------------------------------------------------------------------ *
 * ProductPickerModal — browse + configure in one dialog.
 *
 * The redesign's replacement for ProductTypeSelectorList in components.js.
 * Left pane browses the catalogue (search, type chips, scrolling grid with
 * photos); the right pane configures the selected product and shows a
 * running line total.
 *
 * It talks to the same endpoint the old overlay did — POST
 * /products/getAllBy { finder, page, limit } — and hands back a line object
 * in the same shape ProductTypeSelectorList produced, so the work order
 * payload is unchanged.
 *
 * Props
 *   open        bool
 *   group       which rail group opened it ("Casket cover" | "Tributes")
 *   context     subtitle line, e.g. "Tributes · Margaret Whitcombe"
 *   settings    the /settings/getAll record (sash_prices, product_types)
 *   editLine    optional — a line already on the order. The dialog opens
 *               with its product selected and its size, quantity, price,
 *               lid, sash and comment filled in; submitting hands back the
 *               updated line through onUpdate instead of onAdd.
 *   onAdd(line)
 *   onUpdate(line)
 *   onClose()
 * ------------------------------------------------------------------ */

const TYPE_COLORS = {
  "Casket cover": "var(--rf-brand)",
  Wreath: "var(--rf-brand-lite)",
  Bowl: "var(--rf-brand-soft)",
  Heart: "var(--rf-brand-pale)",
  Sprays: "var(--rf-brand-soft)",
  Cross: "var(--rf-brand-lite)",
  Letters: "var(--rf-brand-pale)",
  "Singles/Petals": "#ebdcbf",
};

const LID_TYPES = [
  "Flat",
  "Single raised",
  "Double raised",
  "Triple raised",
  "Domed",
];

const PAGE_LIMIT = 24;

/* Quantity is typed as well as stepped — thirty singles should not take
   thirty clicks. The box may be empty mid-typing; empty reads as 1. */
const MAX_QTY = 999;
const qtyNum = (a) => Number(a) || 1;
const clampQty = (n) => Math.min(MAX_QTY, Math.max(1, n));

const money = (n) => `$${Number(n || 0).toFixed(2)}`;

/* Products store sizes as a JSON string map { "Full": 420, ... }. The grid
   and the size radio list both want an ordered [label, price] array. */
function sizeList(product) {
  if (!product) return [];
  let parsed = product.sizes;
  if (typeof parsed === "string") {
    try {
      parsed = JSON.parse(parsed || "{}");
    } catch (err) {
      parsed = {};
    }
  }
  return Object.keys(parsed || {}).map((label) => [label, Number(parsed[label])]);
}

export function ProductPickerModal({
  open,
  group = "Tributes",
  context = "",
  settings = {},
  /* the business the order is being created in, when it isn't the open
     one — products are per business */
  businessId,
  /* which product types this section may offer: { only: [...] } for the
     casket cover half, { except: [...] } for tributes (only the Casket
     cover type itself is kept out — singles and the other types filed with
     it are wanted as tributes at times), null for no limit */
  typeRule = null,
  editLine = null,
  onAdd,
  onUpdate,
  onClose,
}) {
  const [query, setQuery] = useState("");
  const [type, setType] = useState("All");
  const [showAllTypes, setShowAllTypes] = useState(false);
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const [pickId, setPickId] = useState(null);
  const [size, setSize] = useState(null);
  const [amount, setAmount] = useState(1);
  const [customPrice, setCustomPrice] = useState("");
  const [lidType, setLidType] = useState("");
  const [sash, setSash] = useState(false);
  const [sashPrice, setSashPrice] = useState(0);
  const [sashMessage, setSashMessage] = useState("");
  const [comment, setComment] = useState(false);
  const [commentText, setCommentText] = useState("");

  /* The line being edited, as a product: saved lines carry their own line
     _id and point at the catalogue through product_id, while lines added
     this session still carry the product's _id. Its sizes are the snapshot
     taken when it was ordered, so the price it was sold at still shows. */
  const editBase = useMemo(() => {
    if (!editLine) return null;
    return {
      ...editLine,
      _id: (editLine.fromDB && editLine.product_id) || editLine._id,
    };
  }, [editLine]);

  /* Casket cover opens pre-filtered; tributes open on everything. An edit
     opens on the line's own type so it sits among its neighbours. */
  useEffect(() => {
    if (!open) return;
    setQuery("");
    if (editBase) {
      setType(editBase.product_type || "All");
      prefill(editBase);
    } else {
      setType(group === "Casket cover" ? "Casket cover" : "All");
      resetConfigure();
    }
    // prefill/resetConfigure only set state
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, group, editBase]);

  const sashPrices = useMemo(() => {
    const raw = (settings.sash_prices || "")
      .split(",")
      .map((each) => Number(each))
      .filter((each) => each > 0);
    /* a saved sash price that has since left the settings list still has
       to be choosable, or the edit would silently reprice it */
    const saved = Number(editLine && editLine.sashPrice) || 0;
    if (saved > 0 && !raw.includes(saved)) raw.push(saved);
    return raw;
  }, [settings.sash_prices, editLine]);

  const types = useMemo(() => {
    let list = ProductTypes;
    if (settings.product_types) {
      try {
        const parsed = JSON.parse(settings.product_types);
        if (Array.isArray(parsed) && parsed.length) list = parsed;
      } catch (err) {
        /* fall back to the built-in list */
      }
    }
    const lower = (t) => String(t || "").trim().toLowerCase();
    if (typeRule && typeRule.only) {
      list = typeRule.only;
    } else if (typeRule && typeRule.except) {
      const out = typeRule.except.map(lower);
      list = list.filter((t) => !out.includes(lower(t)));
    }
    if (editBase && editBase.product_type && !list.includes(editBase.product_type))
      list = [...list, editBase.product_type];
    return ["All", ...list];
  }, [settings.product_types, editBase, typeRule]);

  /* "All" still means all this section may offer */
  const typeFinder =
    type !== "All"
      ? { product_type: type }
      : typeRule && typeRule.only
      ? { product_type: { $in: typeRule.only } }
      : typeRule && typeRule.except
      ? { product_type: { $nin: typeRule.except } }
      : {};
  const typeFinderKey = JSON.stringify(typeFinder);

  /* Same finder the old overlay sent: an optional product_type and a $text
     search, paged server-side. */
  useEffect(() => {
    if (!open) return undefined;
    let live = true;
    const timer = setTimeout(() => {
      setLoading(true);
      const q = query.trim();
      axios
        .post(BASE_SERVER_URL + "/products/getAllBy", {
          finder: {
            ...typeFinder,
            ...(q ? { $text: { $search: `"${q}"` } } : {}),
          },
          page: 1,
          limit: PAGE_LIMIT,
        }, businessId ? { headers: { "business-id": businessId } } : undefined)
        .then((response) => {
          if (!live) return;
          setProducts(response.data.response || []);
          setTotal(response.data.responseLength || 0);
        })
        .catch(() => {
          if (live) {
            setProducts([]);
            setTotal(0);
          }
        })
        .finally(() => {
          if (live) setLoading(false);
        });
    }, 250);
    return () => {
      live = false;
      clearTimeout(timer);
    };
    // typeFinder is rebuilt every render; its key says when it changed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, type, query, businessId, typeFinderKey]);

  const pickedIsEdit = Boolean(editBase && pickId === editBase._id);
  const picked = pickedIsEdit
    ? editBase
    : products.find((p) => p._id === pickId) || null;
  const sizes = sizeList(picked);
  /* the saved size may no longer be on the snapshot — keep it pickable */
  if (
    pickedIsEdit &&
    editBase.size &&
    !sizes.some(([label]) => label === editBase.size)
  ) {
    sizes.push([editBase.size, Number(editBase.price) || 0]);
  }

  /* The edited product stays in the grid even when it isn't on the current
     page of results, so it can be seen selected. */
  const gridProducts =
    editBase &&
    !query.trim() &&
    (type === "All" || type === editBase.product_type) &&
    !products.some((p) => p._id === editBase._id)
      ? [editBase, ...products]
      : products;
  const chosenSize = picked
    ? sizes.find(([label]) => label === size) || sizes[0] || null
    : null;

  const listPrice = chosenSize ? chosenSize[1] : 0;
  const unit = customPrice !== "" ? Number(customPrice) || 0 : listPrice;
  /* $0.00 is a real price (a free item) — what's needed is a price that
     was actually set: a size picked, or a custom price that is a number. */
  const priceSet =
    customPrice !== "" ? !isNaN(Number(customPrice)) : Boolean(chosenSize);
  const freeByCustom = customPrice !== "" && Number(customPrice) === 0;
  const sashUnit = sash ? Number(sashPrice) || 0 : 0;
  /* Line total mirrors the repo's own formula (components.js
     OrdersPreSummaryTable): the sash is charged once per line, not per unit. */
  const lineTotal = unit * qtyNum(amount) + sashUnit;

  function resetConfigure() {
    setPickId(null);
    setSize(null);
    setAmount(1);
    setCustomPrice("");
    setLidType("");
    setSash(false);
    setSashPrice(0);
    setSashMessage("");
    setComment(false);
    setCommentText("");
  }

  function prefill(line) {
    const sashOn = Number(line.sashPrice) > 0 || Boolean(line.sash_message);
    setPickId(line._id);
    setSize(line.size || null);
    setAmount(Number(line.amount) || 1);
    setCustomPrice(
      line.customPrice === "" || line.customPrice == null
        ? ""
        : String(line.customPrice)
    );
    setLidType(line.lidType || "");
    setSash(sashOn);
    setSashPrice(sashOn ? Number(line.sashPrice) || 0 : 0);
    setSashMessage(line.sash_message || "");
    setComment(Boolean(line.comment));
    setCommentText(line.comment || "");
  }

  function pick(product) {
    if (editBase && product._id === editBase._id) {
      prefill(editBase);
      return;
    }
    const first = sizeList(product)[0];
    setPickId(product._id);
    setSize(first ? first[0] : null);
    setAmount(1);
    setCustomPrice("");
    setLidType("");
  }

  function submit() {
    if (!picked) return;
    /* Same product kept on an edit: start from the saved line so its line
       _id, product_id and flags survive. A different product is a fresh
       line, and the page decides what happens to the old one. */
    const line = pickedIsEdit ? { ...editLine } : { ...picked };
    delete line.price;
    /* Every total reads `customPrice || price`, which skips a custom 0 —
       so a line made free carries a 0 list price as well. */
    line.price = freeByCustom ? 0 : listPrice;
    line.size = chosenSize ? chosenSize[0] : "";
    line.amount = Number(amount) || 1;
    line.customPrice = customPrice === "" ? "" : Number(customPrice);
    line.lidType = picked.product_type === "Casket cover" ? lidType : "";
    line.sashPrice = sash ? Number(sashPrice) || 0 : 0;
    line.sash_message = sash ? sashMessage : "";
    line.comment = comment ? commentText : "";
    if (editLine) {
      onUpdate(line);
      return;
    }
    onAdd(line);
    // keep the dialog open so the next tribute can be added
    resetConfigure();
  }

  if (!open) return null;

  return (
    <div className="rf-modal-scrim" onClick={onClose}>
      <div className="rf-modal" onClick={(e) => e.stopPropagation()}>
        <div className="rf-modal-head">
          <div>
            <div className="rf-serif rf-modal-title">
              {editLine ? "Edit product" : "Add product"}
            </div>
            <div className="rf-modal-context">{context}</div>
          </div>
          <span className="rf-spacer" />
          <button
            type="button"
            className="rf-icon-btn"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="rf-modal-body">
          {/* ---------- browse ---------- */}
          <div className="rf-pp-browse">
            <div className="rf-pp-filters">
              <div className="rf-pp-search">
                <span className="rf-ss-icon" />
                <input
                  value={query}
                  placeholder="Search by name or code"
                  onChange={(e) => setQuery(e.target.value)}
                />
              </div>
              <div className="rf-pp-types">
                {(showAllTypes ? types : types.slice(0, 10)).map((t) => (
                  <button
                    key={t}
                    type="button"
                    className={`rf-chip-btn${type === t ? " is-active" : ""}`}
                    onClick={() => setType(t)}
                  >
                    {t}
                  </button>
                ))}
                {types.length > 10 && (
                  <button
                    type="button"
                    className="rf-chip-btn rf-pp-types-toggle"
                    onClick={() => setShowAllTypes((prev) => !prev)}
                  >
                    {showAllTypes
                      ? "Show less"
                      : `Show more (${types.length - 10})`}
                  </button>
                )}
              </div>
            </div>

            <div className="rf-pp-grid-wrap">
              <div className="rf-pp-grid">
                {loading &&
                  Array.from({ length: 9 }).map((_, i) => (
                    <div key={i} className="rf-skeleton rf-pp-skeleton" />
                  ))}

                {!loading &&
                  gridProducts.map((p) => {
                    const on = p._id === pickId;
                    const options = sizeList(p);
                    const low = options.length
                      ? Math.min(...options.map((s) => s[1]))
                      : 0;
                    return (
                      <div
                        key={p._id}
                        className={`rf-pp-card${on ? " is-selected" : ""}`}
                        onClick={() => pick(p)}
                      >
                        <div className="rf-pp-photo">
                          {p.image ? (
                            <ProductPhoto product={p} size="thumb" />
                          ) : (
                            <span className="rf-pp-photo-empty">
                              {p.product_type}
                            </span>
                          )}
                          <span className={`rf-pp-tick${on ? " is-on" : ""}`}>
                            {on ? "✓" : ""}
                          </span>
                        </div>
                        <div className="rf-pp-card-body">
                          <div className="rf-pp-card-top">
                            <span
                              className="rf-pp-accent"
                              style={{
                                background:
                                  TYPE_COLORS[p.product_type] || "var(--rf-brand-soft)",
                              }}
                            />
                            <div>
                              <div className="rf-pp-name">{p.product_name}</div>
                              <div className="rf-pp-code">{p.code}</div>
                            </div>
                          </div>
                          <div className="rf-pp-card-foot">
                            <span>
                              {options.length > 1
                                ? `${options.length} sizes`
                                : "one size"}
                            </span>
                            <span className="rf-pp-price">
                              {options.length > 1
                                ? `from ${money(low)}`
                                : money(low)}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </div>

              {!loading && gridProducts.length === 0 && (
                <div className="rf-ss-empty">
                  No products match this search.
                </div>
              )}
            </div>

            <div className="rf-pp-browse-foot">
              <span>
                {products.length} of {total} products
              </span>
            </div>
          </div>

          {/* ---------- configure — only mounted once a product is picked,
               so browsing gets the full width until then ---------- */}
          {picked && (
            <div className="rf-pp-config">
              <div className="rf-pp-config-scroll">
                  <div className="rf-pp-picked">
                    <div className="rf-pp-thumb">
                      {picked.image ? (
                        <ProductPhoto product={picked} size="medium" guess={pickedIsEdit} />
                      ) : (
                        <span>Photo</span>
                      )}
                    </div>
                    <div>
                      <div className="rf-pp-picked-name">
                        {picked.product_name}
                      </div>
                      <div className="rf-pp-picked-code">
                        {picked.code} · {picked.product_type}
                      </div>
                    </div>
                  </div>

                  <div className="rf-pp-block">
                    <div className="rf-label">Size</div>
                    <div className="rf-pp-sizes">
                      {sizes.map(([label, price]) => {
                        const on = chosenSize && label === chosenSize[0];
                        return (
                          <div
                            key={label}
                            className={`rf-pp-size${on ? " is-on" : ""}`}
                            onClick={() => setSize(label)}
                          >
                            <span className="rf-radio">{on ? "✓" : ""}</span>
                            <span className="rf-pp-size-label">{label}</span>
                            <span className="rf-pp-size-price">
                              {money(price)}
                            </span>
                          </div>
                        );
                      })}
                      {sizes.length === 0 && (
                        <div className="rf-pp-empty-note">
                          This product has no sizes set — enter a custom price
                          below.
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="rf-pp-row2">
                    <div className="rf-pp-qty-wrap">
                      <div className="rf-label">Quantity</div>
                      <div className="rf-stepper">
                        <button
                          type="button"
                          aria-label="One fewer"
                          onClick={() =>
                            setAmount((a) => clampQty(qtyNum(a) - 1))
                          }
                        >
                          −
                        </button>
                        <input
                          type="text"
                          inputMode="numeric"
                          aria-label="Quantity"
                          value={amount}
                          onFocus={(e) => e.target.select()}
                          onChange={(e) => {
                            const digits = e.target.value.replace(/\D/g, "");
                            setAmount(
                              digits === ""
                                ? ""
                                : Math.min(MAX_QTY, Number(digits))
                            );
                          }}
                          onBlur={() => setAmount((a) => clampQty(qtyNum(a)))}
                        />
                        <button
                          type="button"
                          aria-label="One more"
                          onClick={() =>
                            setAmount((a) => clampQty(qtyNum(a) + 1))
                          }
                        >
                          +
                        </button>
                      </div>
                    </div>
                    <div className="rf-pp-custom-wrap">
                      <div className="rf-label">Custom price</div>
                      <div className="rf-prefix-input">
                        <span>$</span>
                        <input
                          inputMode="decimal"
                          value={customPrice}
                          placeholder={money(listPrice).replace("$", "")}
                          onChange={(e) => setCustomPrice(e.target.value)}
                        />
                      </div>
                    </div>
                  </div>

                  {picked.product_type === "Casket cover" && (
                    <div className="rf-pp-block">
                      <div className="rf-label">Lid type</div>
                      <div className="rf-pp-types">
                        {LID_TYPES.map((l) => (
                          <button
                            key={l}
                            type="button"
                            className={`rf-chip-btn${
                              lidType === l ? " is-active" : ""
                            }`}
                            onClick={() =>
                              setLidType((prev) => (prev === l ? "" : l))
                            }
                          >
                            {l}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {(sashPrices.length > 0 || sash) && (
                    <div className="rf-pp-block">
                      <label className="rf-check">
                        <input
                          type="checkbox"
                          checked={sash}
                          onChange={(e) => {
                            setSash(e.target.checked);
                            setSashPrice(
                              e.target.checked ? sashPrices[0] : 0
                            );
                            if (!e.target.checked) setSashMessage("");
                          }}
                        />
                        <span className="rf-check-box" aria-hidden="true">
                          ✓
                        </span>
                        <span className="rf-check-label">Add sash message</span>
                        <span className="rf-check-meta">
                          {sash
                            ? `+${money(sashUnit)}`
                            : `+${money(Math.min(...sashPrices))}–${Number(
                                Math.max(...sashPrices)
                              ).toFixed(2)}`}
                        </span>
                      </label>

                      {sash && (
                        <div className="rf-pp-sash">
                          <div className="rf-pp-types">
                            {sashPrices.map((p) => (
                              <button
                                key={p}
                                type="button"
                                className={`rf-chip-btn is-grow${
                                  Number(sashPrice) === p ? " is-active" : ""
                                }`}
                                onClick={() => setSashPrice(p)}
                              >
                                {money(p)}
                              </button>
                            ))}
                          </div>
                          <textarea
                            rows={2}
                            value={sashMessage}
                            placeholder="Type sash message…"
                            onChange={(e) => setSashMessage(e.target.value)}
                          />
                        </div>
                      )}
                    </div>
                  )}

                  <div className="rf-pp-block">
                    <label className="rf-check">
                      <input
                        type="checkbox"
                        checked={comment}
                        onChange={(e) => {
                          setComment(e.target.checked);
                          if (!e.target.checked) setCommentText("");
                        }}
                      />
                      <span className="rf-check-box" aria-hidden="true">
                        ✓
                      </span>
                      <span className="rf-check-label">
                        Add comment for the bench
                      </span>
                    </label>

                    {comment && (
                      <textarea
                        rows={2}
                        value={commentText}
                        placeholder="e.g. no lilies — family allergy"
                        onChange={(e) => setCommentText(e.target.value)}
                      />
                    )}
                  </div>
                </div>

                <div className="rf-pp-config-foot">
                  <div className="rf-pp-totals">
                    <div className="rf-pp-total-row">
                      <span>
                        {chosenSize ? chosenSize[0] : "Custom"} ×{" "}
                        {qtyNum(amount)}
                      </span>
                      <span>{money(unit * qtyNum(amount))}</span>
                    </div>
                    {sash && (
                      <div className="rf-pp-total-row">
                        <span>Sash message</span>
                        <span>{money(sashUnit)}</span>
                      </div>
                    )}
                    <div className="rf-pp-total-row is-total">
                      <span>Line total</span>
                      <span>{money(lineTotal)}</span>
                    </div>
                  </div>

                  <div className="rf-pp-actions">
                    <button
                      type="button"
                      className="rf-btn rf-btn-ghost"
                      onClick={onClose}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="rf-btn rf-btn-primary rf-pp-add"
                      onClick={submit}
                      disabled={!priceSet}
                    >
                      {editLine ? "Update line" : "Add to order"}
                    </button>
                  </div>
                  {!editLine && (
                    <div className="rf-pp-keep">
                      Adding keeps this panel open so you can add another
                    </div>
                  )}
                </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ProductPickerModal;
